import apiClient from '../../../../shared/api/apiClient'
import { ENDPOINTS } from '../../../../shared/api/endpoints'
import {
  departmentApi,
  type DepartmentResponseDto,
} from '../../../User-Role-Management/Department/api/departmentApi'

export type BackendAttendanceStatus =
  | 'PRESENT'
  | 'ABSENT'
  | 'LATE'
  | 'HALF_DAY'
  | 'ON_LEAVE'

export interface AttendanceRecordStaff {
  name?: string
  code?: string
  employeeId?: string
  firstName?: string
  lastName?: string
  departmentName?: string
  designationName?: string
}

export interface BackendAttendanceRecord {
  id: string
  branchId?: string
  staffId: string
  staff: AttendanceRecordStaff
  attendanceDate: string
  dayOfWeek?: string
  checkInTime?: string | null
  checkOutTime?: string | null
  workingHours?: number | string | null
  status: BackendAttendanceStatus
  source?: string
  remarks?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface AttendanceRecordsQueryParams {
  staffId?: string
  departmentId?: string
  status?: BackendAttendanceStatus
  search?: string
  fromDate?: string // YYYY-MM-DD
  toDate?: string // YYYY-MM-DD
  page?: number
  limit?: number
}

export interface AttendanceRecordsListResponse {
  items: BackendAttendanceRecord[]
  total: number
  page: number
  limit: number
}

export interface EditAttendancePayload {
  checkInTime?: string // "HH:mm"
  checkOutTime?: string // "HH:mm"
  status?: BackendAttendanceStatus
  remarks?: string
}

export interface ExportAttendanceQueryParams extends AttendanceRecordsQueryParams {
  designationId?: string
  format?: 'xlsx' | 'csv'
}

export interface ImportErrorItem {
  row: number
  employeeCode: string
  field?: string
  message: string
}

export interface ImportAttendanceResponse {
  totalRows: number
  processed: number
  created: number
  updated: number
  failed: number
  errors: ImportErrorItem[]
}

export interface DepartmentOption {
  label: string
  value: string
}

/**
 * 1) LIST TABLE: GET /records
 */
export async function getAttendanceRecords(
  params?: AttendanceRecordsQueryParams
): Promise<AttendanceRecordsListResponse> {
  const query: Record<string, string | number> = {}

  if (params?.staffId && params.staffId.trim()) {
    query.staffId = params.staffId.trim()
  }
  if (params?.departmentId && params.departmentId.trim()) {
    query.departmentId = params.departmentId.trim()
  }
  if (params?.status && params.status.trim()) {
    query.status = params.status.trim()
  }
  if (params?.search && params.search.trim()) {
    query.search = params.search.trim()
  }
  if (params?.fromDate && params.fromDate.trim()) {
    query.fromDate = params.fromDate.trim()
  }
  if (params?.toDate && params.toDate.trim()) {
    query.toDate = params.toDate.trim()
  }
  if (typeof params?.page === 'number') {
    query.page = params.page
  }
  if (typeof params?.limit === 'number') {
    query.limit = params.limit
  }

  const response = await apiClient.get<unknown>(ENDPOINTS.HRMS.ATTENDANCE_RECORDS, {
    params: Object.keys(query).length > 0 ? query : undefined,
  })

  const payload = (response.data as { data?: unknown })?.data ?? response.data
  const payloadObj = payload as {
    items?: unknown
    total?: unknown
    page?: unknown
    limit?: unknown
  }

  const items: BackendAttendanceRecord[] = Array.isArray(payload)
    ? (payload as BackendAttendanceRecord[])
    : Array.isArray(payloadObj?.items)
      ? (payloadObj.items as BackendAttendanceRecord[])
      : []

  const total = typeof payloadObj?.total === 'number' ? payloadObj.total : items.length
  const page = typeof payloadObj?.page === 'number' ? payloadObj.page : (params?.page || 1)
  const limit = typeof payloadObj?.limit === 'number' ? payloadObj.limit : (params?.limit || 20)

  return {
    items,
    total,
    page,
    limit,
  }
}

/**
 * 2) EDIT: PATCH /records/{id}
 */
export async function editAttendanceRecord(
  id: string,
  payload: EditAttendancePayload
): Promise<BackendAttendanceRecord> {
  if (!id || !id.trim()) {
    throw new Error('Attendance record ID is required')
  }

  const endpoint = ENDPOINTS.HRMS.ATTENDANCE_RECORD_BY_ID(id.trim())
  const response = await apiClient.patch<unknown>(endpoint, payload)
  const resData = (response.data as { data?: unknown })?.data ?? response.data
  return resData as BackendAttendanceRecord
}

/**
 * 3) EXPORT: GET /records/export
 */
export async function exportAttendanceRecords(
  params?: ExportAttendanceQueryParams
): Promise<{ data: Blob; filename: string }> {
  const query: Record<string, string | number> = {}

  if (params?.staffId && params.staffId.trim()) query.staffId = params.staffId.trim()
  if (params?.departmentId && params.departmentId.trim()) query.departmentId = params.departmentId.trim()
  if (params?.designationId && params.designationId.trim()) query.designationId = params.designationId.trim()
  if (params?.status && params.status.trim()) query.status = params.status.trim()
  if (params?.search && params.search.trim()) query.search = params.search.trim()
  if (params?.fromDate && params.fromDate.trim()) query.fromDate = params.fromDate.trim()
  if (params?.toDate && params.toDate.trim()) query.toDate = params.toDate.trim()
  query.format = params?.format || 'xlsx'

  const response = await apiClient.get(ENDPOINTS.HRMS.ATTENDANCE_RECORDS_EXPORT, {
    params: query,
    responseType: 'blob',
  })

  const contentDisposition =
    response.headers?.['content-disposition'] || response.headers?.['Content-Disposition']
  let filename = 'attendance-records.xlsx'
  if (contentDisposition && typeof contentDisposition === 'string') {
    const match = contentDisposition.match(/filename="?([^";]+)"?/)
    if (match && match[1]) {
      filename = match[1]
    }
  }

  return {
    data: response.data,
    filename,
  }
}

/**
 * 4) DOWNLOAD TEMPLATE: GET /records/import/template
 */
export async function downloadAttendanceImportTemplate(): Promise<{
  data: Blob
  filename: string
}> {
  const response = await apiClient.get(ENDPOINTS.HRMS.ATTENDANCE_IMPORT_TEMPLATE, {
    responseType: 'blob',
  })

  const contentDisposition =
    response.headers?.['content-disposition'] || response.headers?.['Content-Disposition']
  let filename = 'attendance-import-template.xlsx'
  if (contentDisposition && typeof contentDisposition === 'string') {
    const match = contentDisposition.match(/filename="?([^";]+)"?/)
    if (match && match[1]) {
      filename = match[1]
    }
  }

  return {
    data: response.data,
    filename,
  }
}

/**
 * 5) IMPORT: POST /records/import
 */
export async function importAttendanceRecords(file: File): Promise<ImportAttendanceResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiClient.post<unknown>(ENDPOINTS.HRMS.ATTENDANCE_IMPORT, formData)
  const resData = (response.data as { data?: unknown })?.data ?? response.data
  return resData as ImportAttendanceResponse
}

/**
 * ADD-ON: GET /api/departments
 */
export async function getDepartments(): Promise<DepartmentOption[]> {
  const items = await departmentApi.getAllDepartments()
  return [
    { label: 'All Departments', value: '' },
    ...items.map((d) => ({
      label: d.departmentName || (d as any).name || 'Unnamed Department',
      value: d.id,
    })),
  ]
}

/**
 * Helper to download Blob files in browser
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}
